// Detect environment and use appropriate API URLs
const isDevelopment = window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname.includes('localhost')



const API_BASE_URLS = [
  'https://franchisebrigade.com/admin/api',
  'http://localhost:8000/api',
]

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URLS[0] // Start with localhost
    this.currentURLIndex = 0
  }

  async request(endpoint, options = {}) {
    const urls = API_BASE_URLS.map(base => `${base}${endpoint}`)

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i]
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options.headers
        },
        mode: 'cors', // Explicitly set CORS mode
        credentials: 'omit', // Don't send cookies to avoid CORS issues
        ...options
      }

      try {
        const response = await fetch(url, config)

        if (!response.ok) {
          if (i === urls.length - 1) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          continue // Try next URL
        }

        const data = await response.json()
        return data
      } catch (error) {
        if (i === urls.length - 1) {
          // If all URLs failed and we're in development, provide mock response
          if (isDevelopment) {
            return this.getMockResponse(endpoint, options)
          }
          throw error
        }
        // Continue to next URL
      }
    }
  }

  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${endpoint}?${queryString}` : endpoint
    return this.request(url, { method: 'GET' })
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' })
  }

  // Test API connectivity
  async testConnection() {
    try {
      const response = await this.get('/health')
      return true
    } catch (error) {
      return false
    }
  }

  // Mock responses for development when API is not available
  getMockResponse(endpoint, options = {}) {
    const mockData = {
      '/investors': { success: true, message: 'Investor registered successfully (mock)' },
      '/franchises': [
        {
          id: 1,
          business_name: 'Coffee House',
          business_logo: 'https://placehold.co/200x100?text=Coffee+House',
          banner: 'https://placehold.co/1200x400?text=Coffee+House+Banner',
          category: 'Food & Beverage',
          investment_min: 500000,
          investment_max: 1000000,
          excepted_roi_min: 12,
          excepted_roi_max: 18,
          total_outlets: 15,
          treding_type: 'weekly',
          is_active: true,
          status: 'approved',
          description: 'A premium coffee house chain offering the best brews.',
          trending_type: 'weekly'
        },
        {
          id: 2,
          business_name: 'Tech Store',
          business_logo: 'https://placehold.co/200x100?text=Tech+Store',
          banner: 'https://placehold.co/1200x400?text=Tech+Store+Banner',
          category: 'Retail',
          investment_min: 1000000,
          investment_max: 2000000,
          excepted_roi_min: 18,
          excepted_roi_max: 24,
          total_outlets: 8,
          trending_type: 'monthly',
          is_active: true,
          status: 'approved',
          description: 'Your one-stop shop for all things tech.'
        },
        {
          id: 3,
          business_name: 'Wellness Spa',
          business_logo: 'https://placehold.co/200x100?text=Wellness+Spa',
          banner: 'https://placehold.co/1200x400?text=Wellness+Spa+Banner',
          category: 'Health & Wellness',
          investment_min: 1500000,
          investment_max: 3000000,
          excepted_roi_min: 24,
          excepted_roi_max: 36,
          total_outlets: 5,
          trending_type: 'yearly',
          is_active: true,
          status: 'approved',
          description: 'Relax and rejuvenate with our premium spa services.'
        },
        {
          id: 4,
          business_name: 'Burger King',
          business_logo: 'https://placehold.co/200x100?text=Burger+King',
          banner: 'https://placehold.co/1200x400?text=Burger+King+Banner',
          category: 'Food & Beverage',
          investment_min: 2000000,
          investment_max: 5000000,
          excepted_roi_min: 18,
          excepted_roi_max: 30,
          total_outlets: 50,
          trending_type: 'weekly',
          is_active: true,
          status: 'approved',
          description: 'Global fast food chain known for its burgers.'
        },
        {
          id: 5,
          business_name: 'Green Grocers',
          business_logo: 'https://placehold.co/200x100?text=Green+Grocers',
          banner: 'https://placehold.co/1200x400?text=Green+Grocers+Banner',
          category: 'Retail',
          investment_min: 300000,
          investment_max: 800000,
          excepted_roi_min: 6,
          excepted_roi_max: 12,
          total_outlets: 20,
          trending_type: 'monthly',
          is_active: true,
          status: 'approved',
          description: 'Organic and fresh produce market.'
        }
      ],
      '/categories': [
        { id: 1, name: 'Food & Beverage', slug: 'food-beverage' },
        { id: 2, name: 'Retail', slug: 'retail' },
        { id: 3, name: 'Services', slug: 'services' },
        { id: 4, name: 'Health & Wellness', slug: 'health-wellness' },
        { id: 5, name: 'Education', slug: 'education' },
        { id: 6, name: 'Automobile', slug: 'automobile' }
      ],
      '/states': [
        { id: 1, name: 'Andhra Pradesh' },
        { id: 2, name: 'Arunachal Pradesh' },
        { id: 3, name: 'Assam' },
        { id: 4, name: 'Bihar' },
        { id: 5, name: 'Chhattisgarh' },
        { id: 6, name: 'Goa' },
        { id: 7, name: 'Gujarat' },
        { id: 8, name: 'Haryana' },
        { id: 9, name: 'Himachal Pradesh' },
        { id: 10, name: 'Jharkhand' },
        { id: 11, name: 'Karnataka' },
        { id: 12, name: 'Kerala' },
        { id: 13, name: 'Madhya Pradesh' },
        { id: 14, name: 'Maharashtra' },
        { id: 15, name: 'Manipur' },
        { id: 16, name: 'Meghalaya' },
        { id: 17, name: 'Mizoram' },
        { id: 18, name: 'Nagaland' },
        { id: 19, name: 'Odisha' },
        { id: 20, name: 'Punjab' },
        { id: 21, name: 'Rajasthan' },
        { id: 22, name: 'Sikkim' },
        { id: 23, name: 'Tamil Nadu' },
        { id: 24, name: 'Telangana' },
        { id: 25, name: 'Tripura' },
        { id: 26, name: 'Uttar Pradesh' },
        { id: 27, name: 'Uttarakhand' },
        { id: 28, name: 'West Bengal' }
      ],
      '/webinars': [
        {
          id: 1,
          title: 'Franchise Success Stories: 2024 Panel',
          thumbnail: 'https://img.youtube.com/vi/3JZ_D3ELwOQ/hqdefault.jpg',
          video: 'https://www.youtube.com/watch?v=3JZ_D3ELwOQ',
          duration: '55 min'
        },
        {
          id: 2,
          title: 'Digital Marketing for Franchises',
          thumbnail: 'https://img.youtube.com/vi/2vjPBrBU-TM/hqdefault.jpg',
          video: 'https://www.youtube.com/watch?v=2vjPBrBU-TM',
          duration: '40 min'
        }
      ],
      '/business-hours': [
        { day: 'Monday', open_time: '09:00', close_time: '18:00', is_closed: false },
        { day: 'Tuesday', open_time: '09:00', close_time: '18:00', is_closed: false },
        { day: 'Wednesday', open_time: '09:00', close_time: '18:00', is_closed: false },
        { day: 'Thursday', open_time: '09:00', close_time: '18:00', is_closed: false },
        { day: 'Friday', open_time: '09:00', close_time: '18:00', is_closed: false },
        { day: 'Saturday', open_time: '10:00', close_time: '14:00', is_closed: false },
        { day: 'Sunday', open_time: '', close_time: '', is_closed: true }
      ],
      '/pages': [
        { slug: 'privacy-policy', title: 'Privacy Policy', content: 'Privacy Policy Content' },
        { slug: 'terms-conditions', title: 'Terms & Conditions', content: 'Terms Content' }
      ],
      '/contact-info': {
        phone: '+91 9606943373',
        email: 'info@franchisebrigade.com',
        address: 'Janardhan Towers, Residency Road, Bangalore'
      },
      '/settings': {
        site_name: 'Franchise Brigade',
        logo: '/logo.png'
      },
      '/stats': {
        franchises: 1500,
        investors: 5000,
        cities: 100
      }
    }

    return mockData[endpoint] || { success: true, message: 'Mock response' }
  }
}

export default new ApiService() 